import { InvalidInputError } from '../Errors/InvalidInputError.js';
import { UnsupportedFunctionalityError } from '../Errors/UnsupportedFunctionalityError.js';
import { endsWith } from '../Strings/index.js';
import { isArray, isDefined, isFunction, isPlainObject, isString } from '../TypeChecking/index.js';
/**
 * Genera un oggetto le cui chiavi sono i valori presenti nell'array e come valore sempre true
 * @author Riccardo Di Dato
 *
 * @param initial Array di stringhe
 * @returns Oggetto con chiavi i valori di `initial`
 */
export function toHashmap(initial) {
    return initial.reduce((car, cur) => {
        car[cur] = true;
        return car;
    }, {});
}
/**
 * Restituisce l'array rimuovendo le chiavi rimosse.
 * Il metodo non preserva necessariamente l'ordine degli elementi.
 *
 * @author Riccardo Di Dato
 * @param initial Array da cui rimuovere duplicati
 * @returns Array senza duplicati
 */
export function removeDuplicates(initial) {
    return Object.keys(toHashmap(initial));
}
/**
 * Concatena un array di array in un unico array.
 * Non è ricorsiva, opera solo a 2 livelli
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param arr array di array da concatenare
 * @return array finale concatenato
 *
 * @example
 * ```ts
 *      var v = flatten([[1,2],[3,4],[4,5]])
 *      //v = [1,2,3,4,5,6]
 *      var d = flatten([[1,2],[3,4],[4,[5,6]]])
 *      //d = [1,2,3,4,[5,6]]
 * ```
 */
export function flatten(arr) {
    return arr.reduce((carry, cur) => {
        return carry.concat(cur);
    }, []);
}
/**
 *  Funzione per fondere due o più oggetti, è un porting di quella di jquery
 *
 * @link https://github.com/jquery/jquery/blob/master/src/core.js
 * @author Lorenzo Muscariello (lwbu)
 */
export function extend(...args) {
    // took from jQuery core functions
    // https://github.com/jquery/jquery/blob/master/src/core.js
    let options, name, src, copy, copyIsArray, clone, target = args[0] || {}, i = 1, deep = false;
    const length = args.length;
    // Handle a deep copy situation
    if (typeof target === "boolean") {
        deep = target;
        // Skip the boolean and the target
        target = args[i] || {};
        i++;
    }
    // Handle case when target is a string or something (possible in deep copy)
    if (typeof target !== "object" && !isFunction(target)) {
        target = {};
    }
    if (i === length) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        target = this;
        i--;
    }
    for (; i < length; i++) {
        // Only deal with non-null/undefined values
        if ((options = args[i]) != null) {
            // Extend the base object
            for (name in options) {
                src = target[name];
                copy = options[name];
                // Prevent never-ending loop
                if (target === copy) {
                    continue;
                }
                // Recurse if we're merging plain objects or arrays
                if (deep && copy && (isPlainObject(copy) ||
                    (copyIsArray = isArray(copy)))) {
                    if (copyIsArray) {
                        copyIsArray = false;
                        clone = src && isArray(src) ? src : [];
                    }
                    else {
                        clone = src && isPlainObject(src) ? src : {};
                    }
                    // Never move original objects, clone them
                    target[name] = extend(deep, clone, copy);
                    // Don't bring in undefined values
                }
                else if (copy !== undefined) {
                    target[name] = copy;
                }
            }
        }
    }
    // Return the modified object
    return target;
}
/* c8 ignore stop */
/**
 * Converte un intero a 32 bit che rappresenta un colore in formato RGBA nella
 * stringa esadecimale (CSS)
 *
 * ?TODO: La firma non ha i tipi corretti
 *
 * @author Lorenzo Muscariello (lwbu)
 * @param {UInt32|UInt24} color colore espresso come numero intero 32-bit (RGBA)
 * @return Rappresentazione esadecimale RGB del colore preceduta da #
 */
export function rgbaIntegerToCSSHex(color) {
    const ucolor = parseInt(color);
    return ucolor ? '#' + ('000000' + (ucolor & 0xFFFFFF).toString(16)).slice(-6) : '';
}
/**
 * Effettua il parse di una stringa XML in un Documento XML
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param xmlstring Stringa da parsare
 * @return Documento xml parsato
 *
 * @throws {UnsupportedFunctionalityError} se il sistema è in grado di parsare
 * @throws {InvalidInputError} se l'input non è valido
 */
export function parseXML(xmlstring) {
    if (!isString(xmlstring)) {
        throw new InvalidInputError("Invalid input given to parseXML: it must be a string");
    }
    try {
        let xmldoc;
        if (window.DOMParser) {
            const parser = new DOMParser();
            xmldoc = parser.parseFromString(xmlstring, "text/xml");
        }
        else if (window['ActiveXObject']) { // Internet Explorer
            xmldoc = new window['ActiveXObject']("Microsoft.XMLDOM");
            xmldoc.async = false;
            xmldoc.loadXML(xmlstring);
        }
        return xmldoc;
    }
    catch (e) {
        throw new UnsupportedFunctionalityError("This browser doesn't support parseXML", e);
    }
}
/**
 * Trasforma una stringa base64 "url-safe" in un array di byte
 *
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param base64String stringa base 64 url-safe
 * @return array di byte
 */
export function urlB64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}
/**
 * Aggiunge il suffisso di formato all'url di un'immagine.
 * il formato può essere specificato sia come array `[w, h]`,
 * sia come stringa.
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param imageUrl url dell'immagine
 * @param format formato immagine richiesto
 *
 * @return url modificato con l'identificativo di formato
 *
 * @example
 * ```typescript
 *      var x = Utils.selectImageFormat("http://sito.it/img2.png",[100,200]);
 *      //x = http://sito.it/img2-100x200.png
 *      var y = Utils.selectImageFormat("http://sito.it/img2.png","100x200");
 *      // y == x (le due espressioni sono completamente equivalenti
 * ```
 */
export function selectImageFormat(imageUrl, format) {
    let fstring = "";
    if (isArray(format) && format.length == 2) {
        fstring = format.join("x");
    }
    else if (isString(format) && format.length > 0) {
        fstring = "" + format;
    }
    else {
        return imageUrl;
    }
    let rval = imageUrl;
    if (imageUrl.match(/\/[^/]+\.[^/]*$/)) {
        const idxOf = imageUrl.lastIndexOf(".");
        if (idxOf > 0) {
            rval = imageUrl.substring(0, idxOf) + "-" + fstring + imageUrl.substring(idxOf);
        }
    }
    return rval;
}
/**
 * Restituisce l'origin completa della pagina in esecuzione
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @return origin del documento
 *
 * @throws {UnsupportedFunctionalityError}
 */
export function getOrigin() {
    if (!isDefined(window.location)) {
        throw new UnsupportedFunctionalityError("Error: window.location is undefined");
    }
    if (isString(window.location.origin)) {
        return window.location.origin;
    }
    else if (isDefined(window.location.protocol) && isDefined(window.location.hostname)) {
        let protocol = window.location.protocol;
        if (!endsWith(protocol, ":")) {
            protocol += ":";
        }
        return protocol + "//" + window.location.hostname + (isDefined(window.location.port) && window.location.port != "80" ? ":" + window.location.port : "");
    }
    else {
        throw new UnsupportedFunctionalityError("Error: window.location is missing some required informations");
    }
}
/**
 * Restituisce l'anchor e lo parsa come se fosse un prametro url-encoded
 *
 * @note Questo metodo sostituisce il metodo {@link getParentUrl} di lwbu
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @return valore dell'anchor url decoded
 *
 * @example
 * Se la location corrente è
 * > http://test.it/#http://example.com/peixe
 *
 * il metodo restituirà:
 * > http://example.com/peixe
 *
 * ```
 *      // document.location.href = http://test.it/#http://example.com/peixe
 *      var b = Utils.getParentUrl() // http://example.com/peixe
 * ```
 */
export function getLocationAnchor() {
    return decodeURIComponent(document.location.hash.replace(/^#/, ''));
}
/* c8 ignore start */
/**
 * Restituisce l'anchor e lo parsa come se fosse un prametro url-encoded
 *
 * @deprecated Deprecato in favore di {@link getLocationAnchor}
 * @warn Questo metodo ha un nome ingannevole, per questa ragione è stato deprecato in favore di {@link getLocationAnchor}
 *
 * @return valore dell'anchor url decoded
 *
 * @example
 * ```
 *      //document.location.href = http://test.it/#http://example.com/peixe
 *      var b = Utils.getParentUrl()
 *      //b = http://example.com/peixe
 * ```
 */
export function getParentUrl() {
    return getLocationAnchor();
}
/**
 * Prende l'anchor e lo parsa come se fosse un paraemtro url encoded.
 * Se è un url restituisce l'host con il protocollo privato del path
 *
 * @author Lorenzo Muscariello (lwbu)
 * @return protcol://host
 *
 * @example
 * ```
 *      //document.location.href = http://test.it/#http://example.com/peixe
 *      var b = Utils.getParentBaseUrl()
 *      //b = http://example.com
 * ```
 */
export function getParentBaseUrl() {
    let ref, ref1;
    return (ref = getLocationAnchor()) != null ? (ref1 = ref.match(/^http[s]?:\/\/[^/]+/)) != null ? ref1[0] : void 0 : void 0;
}
function S4() {
    return Math.floor(Math.random() * 0x10000 /* 65536 */).toString(16);
}
/**
 * Genera un id alfanumerico casuale
 *
 * @returns nuovo identificativo alfanumerico
 */
export function guid() {
    return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
}
/* c8 ignore stop */
/**
 * Determina se un oggetto ha una proprietà
 * @param targ
 * @param args
 * @returns
 */
export function hasOwnProperty(targ, prop) {
    return Object.prototype.hasOwnProperty.call(targ, prop);
}
/**
 * Determina se un oggetto ha le chiavi passate al metodo
 * @param targ
 * @param args
 * @returns
 */
export function hasOwnProperties(targ, props) {
    return props.reduce((carry, cur) => carry && hasOwnProperty(targ, cur), true);
}
/**
 * Genera un nuovo oggetto contenente solo le chiavi
 * Se riceve una chiave inesistente la crea con valore undefined
 */
export function pick(targ, keys) {
    return Object.fromEntries(keys.map(k => [k, targ[k]]));
}
export default guid;
